import { deepMerge } from '../../scripts/deepMerge';

export interface HttpRequestConfig {
  headers?: Record<string, string>;
  timeout?: number;
  withCredentials?: boolean;
}

export interface HttpClientResponse<T> {
  data: T;
  status: number;
  statusText: string;
}

export class HttpError extends Error {
  public readonly response: HttpClientResponse<unknown>;

  public get status(): number {
    return this.response.status;
  }

  public constructor(response: HttpClientResponse<unknown>) {
    super(`Request failed with status ${response.status}`);
    this.name = 'HttpError';
    this.response = response;
  }
}

const ensureTrailingSlash = (url: string): string => url.replace(/\/?$/, '/');

const removeSlash = (url: string): string => {
  return url.indexOf('/') === 0 ? url.substring(1) : url;
};

class HttpClient {
  private baseUrl: string;

  private middleware: ((
    config: HttpRequestConfig,
  ) => HttpRequestConfig | Promise<HttpRequestConfig>)[] = [];

  private baseConfig: HttpRequestConfig = {
    timeout: 20000,
    headers: {
      'country-Language': `${CSS_CONFIG.country}-${CSS_CONFIG.language}`,
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  };

  public constructor(baseUrl = '') {
    this.baseUrl = ensureTrailingSlash(baseUrl);
  }

  public applyMiddleware = async (
    initialConfig: HttpRequestConfig = {},
  ): Promise<HttpRequestConfig> =>
    this.middleware.reduce<Promise<HttpRequestConfig>>(
      (config, applyChanges) => config.then(applyChanges),
      Promise.resolve(deepMerge(this.baseConfig, initialConfig)),
    );

  public get = async <T>(
    url: string,
    config?: HttpRequestConfig,
  ): Promise<HttpClientResponse<T>> => {
    return this.request<T>('GET', url, undefined, config);
  };

  public post = async <T, E>(
    url: string,
    body: E,
    config?: HttpRequestConfig,
  ): Promise<HttpClientResponse<T>> => {
    return this.request<T>('POST', url, body, config);
  };

  public put = async <T, E>(
    url: string,
    body: E,
    config?: HttpRequestConfig,
  ): Promise<HttpClientResponse<T>> => {
    return this.request<T>('PUT', url, body, config);
  };

  public delete = async <T>(
    url: string,
    config?: HttpRequestConfig,
  ): Promise<HttpClientResponse<T>> => {
    return this.request<T>('DELETE', url, undefined, config);
  };

  private request = async <T>(
    method: string,
    url: string,
    body?: unknown,
    config?: HttpRequestConfig,
  ): Promise<HttpClientResponse<T>> => {
    const finalConfig = await this.applyMiddleware(config);
    const finalUrl = this.baseUrl + removeSlash(url);
    const headers = new Headers(finalConfig.headers);
    const isFormData = body instanceof FormData;

    if (isFormData) {
      headers.delete('Content-Type');
    }

    const controller = new AbortController();
    const timeout = finalConfig.timeout
      ? setTimeout(() => controller.abort(), finalConfig.timeout)
      : undefined;

    try {
      const response = await fetch(finalUrl, {
        method,
        headers,
        credentials: finalConfig.withCredentials ? 'include' : 'same-origin',
        signal: controller.signal,
        ...(body !== undefined && {
          body:
            isFormData ||
            typeof body === 'string' ||
            body instanceof Blob ||
            body instanceof URLSearchParams
              ? body
              : JSON.stringify(body),
        }),
      });
      const responseText = await response.text();
      let data: unknown = responseText;
      if (responseText) {
        try {
          data = JSON.parse(responseText);
        } catch {
          data = responseText;
        }
      }

      const result: HttpClientResponse<T> = {
        data: data as T,
        status: response.status,
        statusText: response.statusText,
      };

      if (!response.ok) {
        throw new HttpError(result);
      }

      return result;
    } finally {
      if (timeout) clearTimeout(timeout);
    }
  };
}

export default HttpClient;
